import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ActivityIndicator,
  FlatList,
  Image,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { SessionContext } from "../../../contexts";
import {
  getBeforeAfter,
  TGetBeforeAfterResponse,
} from "../../../services/api-clinic";
import GalleryModal from "./GalleryModal";

const tokens = {
  color: {
    text: "#0F172A",
    muted: "#64748B",
    surface: "#F8FAFC",
    border: "#E2E8F0",
    focus: "#2563EB",
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32,
  },
  radius: {
    sm: 8,
    md: 12,
    lg: 16,
  },
  shadow: {
    none: 0,
  },
  font: {
    base: "System",
  },
  fontSize: {
    sm: 14,
    md: 16,
  },
  lineHeight: {
    sm: 20,
    md: 24,
  },
  zIndex: {
    base: 1,
  },
  motion: {
    microMs: 100,
    standardMs: 200,
    complexMs: 300,
  },
};

const TARGET_TILE_WIDTH = 220;

const getConcatUrls = (
  batch: TGetBeforeAfterResponse["batch_images"][number],
) => {
  const urls: string[] = [];
  batch.upload_types.forEach((type, index) => {
    if (type === "CONCAT") {
      const url = batch.urls[index];
      if (url) urls.push(url);
    }
  });
  return urls;
};

const SideBySideGalleryView: React.FC<{
  patientUuid: string;
  availableWidth?: number;
}> = ({ patientUuid, availableWidth }) => {
  const session = useContext(SessionContext)?.session;
  const { width: screenWidth } = useWindowDimensions();
  const [containerWidth, setContainerWidth] = useState(0);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const effectiveWidth = Math.max(
    0,
    availableWidth || containerWidth || screenWidth,
  );
  const gap = tokens.spacing.sm;
  const numColumns = Math.max(
    1,
    Math.floor(effectiveWidth / (TARGET_TILE_WIDTH + gap)),
  );
  const tileWidth = Math.max(
    0,
    Math.floor((effectiveWidth - (numColumns - 1) * gap) / numColumns),
  );
  const tileHeight = Math.max(0, Math.floor(tileWidth / 2));

  const fetchSideBySideImages = useCallback(async () => {
    if (!session) return;
    setIsLoading(true);
    setError(null);

    try {
      const response = await getBeforeAfter(session, {
        is_uploaded: true,
        upload_types: ["CONCAT"],
        patient_uuid: patientUuid,
        limit: 200,
        offset: 0,
        sort_order: "desc",
      });

      const concatUrls = response.batch_images
        .filter((batch) => batch.patient_uuid === patientUuid)
        .flatMap(getConcatUrls);

      const uniqueUrls = Array.from(new Set(concatUrls));
      setImageUrls(uniqueUrls);
    } catch (err) {
      setError("Unable to load side-by-side images.");
    } finally {
      setIsLoading(false);
    }
  }, [session, patientUuid]);

  useEffect(() => {
    setImageUrls([]);
    setModalVisible(false);
    setSelectedIndex(0);
    setError(null);
  }, [patientUuid]);

  useEffect(() => {
    fetchSideBySideImages();
  }, [fetchSideBySideImages]);

  const emptyState = useMemo(() => {
    if (isLoading) return null;
    if (error) {
      return <Text style={styles.statusText}>{error}</Text>;
    }
    if (imageUrls.length === 0) {
      return <Text style={styles.statusText}>No side-by-side images yet.</Text>;
    }
    return null;
  }, [isLoading, error, imageUrls.length]);

  return (
    <View
      style={styles.container}
      onLayout={(event) => {
        if (!availableWidth) {
          setContainerWidth(event.nativeEvent.layout.width);
        }
      }}
    >
      {isLoading && (
        <View style={styles.loadingRow}>
          <ActivityIndicator color={tokens.color.focus} />
          <Text style={styles.statusText}>Loading side-by-side images...</Text>
        </View>
      )}

      {emptyState}

      {!isLoading && !error && imageUrls.length > 0 && (
        <FlatList
          key={`side-by-side-${numColumns}`}
          data={imageUrls}
          keyExtractor={(item, index) => `${item}-${index}`}
          numColumns={numColumns}
          scrollEnabled={false}
          columnWrapperStyle={numColumns > 1 ? { gap } : undefined}
          contentContainerStyle={styles.gridContainer}
          renderItem={({ item, index }) => (
            <Pressable
              accessibilityLabel={`Open side-by-side image ${index + 1}`}
              onPress={() => {
                setSelectedIndex(index);
                setModalVisible(true);
              }}
              style={[styles.imageWrapper, { width: tileWidth }]}
            >
              <Image
                source={{ uri: item }}
                style={[styles.image, { height: tileHeight }]}
                resizeMode="cover"
              />
            </Pressable>
          )}
        />
      )}

      <GalleryModal
        visible={modalVisible}
        imageUrls={imageUrls}
        selectedIndex={selectedIndex}
        onClose={() => setModalVisible(false)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: tokens.spacing.md,
  },
  loadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: tokens.spacing.sm,
  },
  statusText: {
    color: tokens.color.muted,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
  gridContainer: {
    gap: tokens.spacing.sm,
  },
  imageWrapper: {
    borderRadius: tokens.radius.md,
    overflow: "hidden",
    backgroundColor: tokens.color.surface,
    borderWidth: 1,
    borderColor: tokens.color.border,
  },
  image: {
    width: "100%",
  },
});

export default SideBySideGalleryView;
